import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Globe, Flame, ShieldCheck } from "lucide-react";
import { SectionHeading } from "@/components/ui-burly/SectionHeading";
import { ProductCard } from "@/components/ui-burly/ProductCard";
import { PRODUCTS, CATEGORIES, FINISHES } from "@/data/products";
import { ARTICLES } from "@/data/blog";
import { WHY_BURLY, MANUFACTURING_STEPS, CERTIFICATIONS, TESTIMONIALS } from "@/data/extras";
import { SITE, FACTORY_IMG, LIFESTYLE_KITCHEN_NAVY, PRODUCT_UTILITY_CYLINDERS, PRODUCT_DOUBLE_BOWL_POTS, PRODUCT_FLUTED_BRONZE, MFG_PHOTOS, HERO_LIFESTYLE } from "@/data/site";
import { useSeo } from "@/lib/seo";

const HERO_IMG = HERO_LIFESTYLE[0].src;

export default function HomePage() {
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setHeroIdx((i) => (i + 1) % HERO_LIFESTYLE.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  useSeo({
    title: SITE.tagline,
    description: SITE.short_pitch,
    image: HERO_IMG,
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE.brand,
      url: `https://${SITE.domain}`,
      description: SITE.tagline,
      email: SITE.email,
      telephone: SITE.phone_display,
      address: {
        "@type": "PostalAddress",
        streetAddress: SITE.address.line1,
        addressLocality: SITE.address.line2,
        addressRegion: SITE.address.region,
        addressCountry: "IN",
      },
    },
  });

  const featured = PRODUCTS.slice(0, 4);

  return (
    <>
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative bg-[#F0F1F8] border-b border-[#E2E3EE] overflow-hidden"
      >
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-6">
              <div className="text-xs uppercase tracking-[0.3em] font-semibold text-[#282760]">
                Est. {SITE.founded} · {SITE.brand_line}
              </div>
              <h1
                data-testid="hero-title"
                className="mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-[#1A1B2E]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                Premium Fine<br />
                Fireclay Sinks.
              </h1>
              <div
                className="mt-5 text-2xl sm:text-3xl text-[#282760]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                Designed in the USA. Crafted in India.
              </div>
              <p className="mt-8 max-w-xl text-lg leading-relaxed text-[#5B5E78]">
                {SITE.short_pitch}
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/products/"
                  data-testid="hero-cta-products"
                  className="inline-flex items-center gap-2 px-7 py-4 bg-[#1A1B2E] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#282760] transition-colors"
                >
                  Explore the catalog <ArrowRight size={16} />
                </Link>
                <Link
                  to="/contact/"
                  data-testid="hero-cta-contact"
                  className="inline-flex items-center gap-2 px-7 py-4 border border-[#1A1B2E] text-[#1A1B2E] text-xs uppercase tracking-[0.2em] hover:bg-[#1A1B2E] hover:text-white transition-colors"
                >
                  Request a quote
                </Link>
              </div>
              <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
                <Stat label="Established" value={`${SITE.founded}`} testId="hero-stat-established" />
                <Stat label="Designed in" value="USA" testId="hero-stat-designed" />
                <Stat label="Crafted in" value="INDIA" testId="hero-stat-crafted" />
              </div>
            </div>

            <div className="lg:col-span-6 relative">
              <div className="aspect-[4/5] overflow-hidden bg-[#FFFFFF] border border-[#E2E3EE] relative" data-testid="hero-carousel">
                {HERO_LIFESTYLE.map((slide, i) => (
                  <img
                    key={slide.src}
                    src={slide.src}
                    alt={slide.alt}
                    data-testid={`hero-slide-${i}`}
                    loading={i === 0 ? "eager" : "lazy"}
                    fetchPriority={i === 0 ? "high" : "low"}
                    width="1400"
                    height="1167"
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
                      i === heroIdx ? "opacity-100" : "opacity-0"
                    }`}
                  />
                ))}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10" data-testid="hero-carousel-dots">
                  {HERO_LIFESTYLE.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setHeroIdx(i)}
                      data-testid={`hero-dot-${i}`}
                      aria-label={`Go to hero slide ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all ${
                        i === heroIdx ? "w-6 bg-white" : "w-1.5 bg-white/60"
                      }`}
                    />
                  ))}
                </div>
              </div>
              <div className="hidden md:block absolute -bottom-6 -left-8 bg-[#FFFFFF] border border-[#E2E3EE] p-4 max-w-[190px]">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#282760] font-semibold">
                  Trusted by
                </div>
                <div
                  className="mt-1.5 text-xl text-[#1A1B2E] leading-snug"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                >
                  Architects, distributors &amp; homeowners
                </div>
                <p className="mt-1.5 text-xs text-[#5B5E78]">
                  North America, Europe, Australia &amp; the Middle East.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY BURLY */}
      <section data-testid="why-burly-section" className="py-24 lg:py-32 border-b border-[#E2E3EE]">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <SectionHeading
                eyebrow="Why Burly"
                title="Material first. Marketing second."
                sub="We've spent two decades refining one thing, fireclay. Not steel, not composite, not stone. Just clay, glaze, and the kiln."
                testId="why-burly-heading"
              />
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E2E3EE]">
              {WHY_BURLY.map((w, i) => {
                const Icon = [Flame, Award, Globe, ShieldCheck][i % 4];
                return (
                <div
                  key={w.title}
                  data-testid={`why-card-${i}`}
                  className="bg-[#FFFFFF] p-8 lg:p-10"
                >
                  <div className="flex items-center gap-3">
                    <Icon size={18} className="text-[#282760]" />
                    <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1A1B2E]">
                      0{i + 1}
                    </div>
                  </div>
                  <div
                    className="mt-6 text-2xl text-[#1A1B2E] tracking-tight"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                  >
                    {w.title}
                  </div>
                  <p className="mt-3 text-[#5B5E78] leading-relaxed">{w.body}</p>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section data-testid="featured-products" className="py-24 lg:py-32 border-b border-[#E2E3EE]">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <SectionHeading
              eyebrow="Featured"
              title="The current catalog."
              sub="Eight signature sinks from our farmhouse, undermount, topmount and bathroom lines. Built to spec, shipped worldwide."
            />
            <Link
              to="/products/"
              data-testid="featured-view-all"
              className="text-xs uppercase tracking-[0.2em] text-[#1A1B2E] border-b border-[#1A1B2E] pb-1 hover:text-[#282760] hover:border-[#282760] transition-colors self-start sm:self-end"
            >
              View full catalog →
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* MANUFACTURING BENTO */}
      <section data-testid="manufacturing-section" className="py-24 lg:py-32 bg-[#F0F1F8] border-b border-[#E2E3EE]">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Manufacturing"
            title="Six steps. One fire."
            sub="Every Burly sink moves through six tightly-controlled stages over roughly seven days inside our own plant."
          />

          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {MFG_PHOTOS.map((p, i) => (
              <figure
                key={i}
                data-testid={`mfg-photo-${i}`}
                className="group border border-[#E2E3EE] bg-[#FFFFFF] overflow-hidden"
              >
                <div className="aspect-square overflow-hidden bg-[#F0F1F8]">
                  <img
                    src={p.src}
                    alt={p.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-all duration-700 group-hover:scale-105"
                    style={{ filter: "grayscale(100%) contrast(1.05)" }}
                  />
                </div>
                <figcaption className="p-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#282760] font-semibold">
                    Step {String(i + 1).padStart(2, "0")}
                  </div>
                  <div
                    className="mt-1 text-lg text-[#1A1B2E]"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                  >
                    {p.caption}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6">
            {MANUFACTURING_STEPS.slice(0, 4).map((s, i) => (
              <div
                key={s.n}
                data-testid={`mfg-step-${i}`}
                className="lg:col-span-6 bg-[#FFFFFF] border border-[#E2E3EE] p-8"
              >
                <div className="flex items-baseline gap-4">
                  <div
                    className="text-4xl text-[#282760]"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                  >
                    {s.n}
                  </div>
                  <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1A1B2E]">
                    {s.title}
                  </div>
                </div>
                <p className="mt-4 text-[#5B5E78] leading-relaxed max-w-2xl">{s.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <Link
              to="/manufacturing/"
              data-testid="mfg-view-all"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#1A1B2E] hover:text-[#282760] transition-colors"
            >
              See the full process <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section data-testid="certifications-section" className="py-24 lg:py-28 border-b border-[#E2E3EE]">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <SectionHeading
              eyebrow="Certifications"
              title="Independently verified."
              sub="We hold the certifications that matter for global plumbing standards, environmental compliance, and quality systems."
            />
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#E2E3EE] border border-[#E2E3EE]">
            {CERTIFICATIONS.map((c) => (
              <div
                key={c.code}
                data-testid={`cert-${c.code.replace(/\s+|:|\./g, "-")}`}
                className="bg-[#FFFFFF] p-6 min-h-[140px] flex flex-col justify-between"
              >
                <div className="text-sm text-[#1A1B2E] font-semibold">{c.code}</div>
                <div className="text-xs text-[#5B5E78] mt-2">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL EXPORT */}
      <section data-testid="global-export-section" className="py-24 lg:py-32 bg-[#1A1B2E] text-[#E2E3EE]">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.3em] font-semibold text-[#282760]">
                Global Export
              </div>
              <h2
                className="mt-6 text-5xl tracking-tight leading-tight text-white"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                Built for global kitchens.
              </h2>
              <p className="mt-6 text-[#B7B8D0] leading-relaxed max-w-xl">
                Designed in the USA and crafted in India for architects, distributors, and homeowners
                across North America, Europe, Australia, and the Middle East.
              </p>
              <Link
                to="/contact/"
                data-testid="global-export-cta"
                className="mt-10 inline-flex items-center gap-2 px-6 py-3 border border-[#E2E3EE] text-[#E2E3EE] text-xs uppercase tracking-[0.2em] hover:bg-[#282760] hover:border-[#282760] hover:text-white transition-colors"
              >
                Become an associate <ArrowRight size={14} />
              </Link>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-px bg-[#1A1B2E]">
              {[
                { k: "Designed in", v: "USA" },
                { k: "Crafted in", v: "India" },
                { k: "Material", v: "Fine Fireclay" },
                { k: "Body wall", v: "12–45 mm" },
                { k: "Warranty", v: "10 yrs" },
                { k: "Established", v: `${SITE.founded}` },
              ].map((s) => (
                <div key={s.k} className="bg-[#1A1B2E] p-8">
                  <div
                    className="text-4xl text-white"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                  >
                    {s.v}
                  </div>
                  <div className="mt-2 text-xs uppercase tracking-[0.25em] text-[#B7B8D0]">
                    {s.k}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section data-testid="categories-section" className="py-24 lg:py-32 border-b border-[#E2E3EE]">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Product Categories"
            title="Built for every kind of kitchen."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-[#E2E3EE] border border-[#E2E3EE]">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/products/?cat=${c.slug}`}
                data-testid={`category-${c.slug}`}
                className="group bg-[#FFFFFF] p-8 hover:bg-[#F0F1F8] transition-colors flex flex-col justify-between min-h-[200px]"
              >
                <div className="text-xs uppercase tracking-[0.25em] text-[#282760] font-semibold">
                  Category
                </div>
                <div>
                  <div
                    className="text-2xl text-[#1A1B2E] tracking-tight"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                  >
                    {c.name}
                  </div>
                  <div className="mt-3 text-sm text-[#5B5E78]">{c.blurb}</div>
                  <div className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#1A1B2E] group-hover:text-[#282760] transition-colors">
                    Explore <ArrowRight size={12} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* IN USE, Lifestyle gallery */}
      <section data-testid="lifestyle-section" className="py-24 lg:py-32 border-b border-[#E2E3EE]">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <SectionHeading
              eyebrow="Burly in use"
              title="Built for real kitchens."
              sub="Our sinks are designed to hold the day, the stockpot, the goblets, the prep clutter, and still look honest in your photographs."
            />
          </div>
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <figure data-testid="lifestyle-img-1" className="group overflow-hidden border border-[#E2E3EE] bg-[#FFFFFF]">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={LIFESTYLE_KITCHEN_NAVY}
                  alt="Burly fluted apron-front fireclay sink in a contemporary navy kitchen with mountain view"
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <figcaption className="p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#282760] font-semibold">Kitchen</div>
                <div className="mt-2 text-lg text-[#1A1B2E]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                  Shaker 30 · Gloss Alabaster
                </div>
              </figcaption>
            </figure>
            <figure data-testid="lifestyle-img-2" className="group overflow-hidden border border-[#E2E3EE] bg-[#FFFFFF]">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={PRODUCT_UTILITY_CYLINDERS}
                  alt="Stainless cookware and stemware loaded into a Burly fireclay double-bowl sink"
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <figcaption className="p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#282760] font-semibold">Capacity</div>
                <div className="mt-2 text-lg text-[#1A1B2E]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                  10-inch depth, full load
                </div>
              </figcaption>
            </figure>
            <figure data-testid="lifestyle-img-3" className="group overflow-hidden border border-[#E2E3EE] bg-[#FFFFFF]">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={PRODUCT_DOUBLE_BOWL_POTS}
                  alt="Burly double-bowl reversible fireclay sink with mixed cookware and dishware"
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <figcaption className="p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#282760] font-semibold">Double bowl</div>
                <div className="mt-2 text-lg text-[#1A1B2E]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                  Mason 33 · two-handed prep
                </div>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* COLORS & FINISHES */}
      <section data-testid="finishes-section" className="py-24 lg:py-32 bg-[#F0F1F8] border-b border-[#E2E3EE]">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="Colors & Finishes"
                title="Six glazes. One ceramic body."
                sub="Each glaze is hand sprayed and fired into the body, never coated on. The result is a colour that ages with the kitchen, not against it."
              />
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-6">
              {FINISHES.map((f) => (
                <div
                  key={f.id}
                  data-testid={`finish-${f.id}`}
                  className="border border-[#E2E3EE] bg-[#FFFFFF]"
                >
                  <div
                    className="aspect-square"
                    style={{ backgroundColor: f.hex }}
                  />
                  <div className="p-4">
                    <div className="text-sm font-semibold text-[#1A1B2E]">{f.name}</div>
                    <div className="text-xs text-[#5B5E78] mt-1">{f.hex.toUpperCase()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="py-24 lg:py-32 border-b border-[#E2E3EE]">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Said about us"
            title="Three quiet endorsements."
            align="center"
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={t.author}
                data-testid={`testimonial-${i}`}
                className="border border-[#E2E3EE] bg-[#FFFFFF] p-8 flex flex-col justify-between min-h-[280px]"
              >
                <blockquote
                  className="text-lg leading-relaxed text-[#1A1B2E]"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-6 pt-6 border-t border-[#E2E3EE]">
                  <div className="text-sm font-semibold text-[#1A1B2E]">{t.author}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* LATEST ARTICLES */}
      <section data-testid="articles-section" className="py-24 lg:py-32 border-b border-[#E2E3EE]">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <SectionHeading eyebrow="Journal" title="Notes from the kiln." />
            <Link
              to="/blog/"
              data-testid="journal-view-all"
              className="text-xs uppercase tracking-[0.2em] text-[#1A1B2E] border-b border-[#1A1B2E] pb-1 hover:text-[#282760] hover:border-[#282760] transition-colors self-start sm:self-end"
            >
              All articles →
            </Link>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {ARTICLES.slice(0, 6).map((a) => (
              <Link
                key={a.slug}
                to={`/blog/${a.slug}/`}
                data-testid={`article-card-${a.slug}`}
                className="group block border border-[#E2E3EE] bg-[#FFFFFF] hover:border-[#1A1B2E] transition-colors"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#F0F1F8]">
                  <img src={a.cover} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#282760]">
                    {a.category} · {a.read_time} min read
                  </div>
                  <div
                    className="mt-3 text-xl tracking-tight text-[#1A1B2E]"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                  >
                    {a.title}
                  </div>
                  <div className="mt-2 text-sm text-[#5B5E78]">{a.excerpt}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section data-testid="contact-cta-section" className="py-24 lg:py-32 bg-[#282760] text-white">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] font-semibold text-white/80">
              Build with Burly
            </div>
            <h2
              className="mt-6 text-5xl lg:text-6xl tracking-tight leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              Talk to a fireclay specialist.
            </h2>
            <p className="mt-6 text-white/90 leading-relaxed max-w-xl">
              Designers, dealers, developers, share your project and we'll quote within one business day.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link
              to="/contact/"
              data-testid="cta-section-contact"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#1A1B2E] text-white text-xs uppercase tracking-[0.2em] hover:bg-white hover:text-[#1A1B2E] transition-colors"
            >
              Request a quote <ArrowRight size={14} />
            </Link>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="cta-section-mailto"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-white text-white text-xs uppercase tracking-[0.2em] hover:bg-white hover:text-[#282760] transition-colors"
            >
              Email {SITE.email}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

const Stat = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <div className="text-[10px] uppercase tracking-[0.25em] text-[#5B5E78]">{label}</div>
    <div
      className="mt-1 text-3xl text-[#1A1B2E]"
      style={{
        fontFamily: "'Cormorant Garamond', serif",
        fontWeight: 500,
        fontVariantNumeric: "lining-nums",
        fontFeatureSettings: "'lnum' 1",
      }}
    >
      {value}
    </div>
  </div>
);
